import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PositionForm from './PositionForm';
import {
    Briefcase,
    PlusCircle,
    Edit,
    Trash,
    AlertCircle,
    CheckCircle,
    Users,
    ArrowRightLeft,
    CheckCircle2,
    XCircle
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './common/DataTable';
import InfoModal from './common/InfoModal';

export default function PositionManagement() {
    const [positionList, setPositionList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    // Silme & Aktarım Modal State'leri
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [positionToDelete, setPositionToDelete] = useState(null);
    const [transferTargetId, setTransferTargetId] = useState('');
    const [deleteModalError, setDeleteModalError] = useState(null);
    const [forceTransferRequired, setForceTransferRequired] = useState(false);

    // Sadece Personel Aktarımı (Pozisyonu Silmeden) Modal State'i
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [positionToTransfer, setPositionToTransfer] = useState(null);
    const [transferOnlyTargetId, setTransferOnlyTargetId] = useState('');
    const [transferModalError, setTransferModalError] = useState(null);

    const { isAuthenticated, permissions = [], activeSubCompanyCode } = useAuth();

    const canView = useMemo(() => permissions.includes('positions:view_list'), [permissions]);
    const canCreate = useMemo(() => permissions.includes('positions:create'), [permissions]);
    const canUpdate = useMemo(() => permissions.includes('positions:update'), [permissions]);
    const canDelete = useMemo(() => permissions.includes('positions:delete'), [permissions]);

    const fetchPositions = useCallback(async () => {
        if (!canView) {
            setError("Pozisyonları listeleme yetkiniz bulunmamaktadır.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient('Position');
            setPositionList(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || "Pozisyonları getirirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [canView, activeSubCompanyCode]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchPositions();
        }
    }, [isAuthenticated, fetchPositions]);

    const summaryStats = useMemo(() => {
        const total = positionList.length;
        const activePos = positionList.filter(p => p.isActive).length;
        const passivePos = total - activePos;
        const totalActiveEmployees = positionList.reduce((acc, p) => acc + (p.activeEmployeeCount || 0), 0);
        const occupiedPos = positionList.filter(p => (p.activeEmployeeCount || 0) > 0).length;
        return { total, activePos, passivePos, totalActiveEmployees, occupiedPos };
    }, [positionList]);

    const handleAddClick = () => {
        setSelectedPosition(null);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleEditClick = (position) => {
        setSelectedPosition(position);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleTransferClick = (position) => {
        setPositionToTransfer(position);
        setTransferOnlyTargetId('');
        setTransferModalError(null);
        setIsTransferModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleDeleteRequest = (position) => {
        setPositionToDelete(position);
        setTransferTargetId('');
        setDeleteModalError(null);
        setForceTransferRequired((position.totalEmployeeCount || 0) > 0);
        setIsConfirmModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleDeleteConfirm = async () => {
        if (!positionToDelete) return;

        const needsTransfer = forceTransferRequired || (positionToDelete.totalEmployeeCount || 0) > 0;
        if (needsTransfer && !transferTargetId) {
            setDeleteModalError("Bağlı personellerin aktarılacağı hedef pozisyonu seçmelisiniz.");
            return;
        }

        setActionLoading(true);
        setDeleteModalError(null);
        try {
            const endpoint = transferTargetId
                ? `Position/${positionToDelete.id}?transferToPositionId=${transferTargetId}`
                : `Position/${positionToDelete.id}`;

            await apiClient(endpoint, { method: 'DELETE' });
            setIsConfirmModalOpen(false);
            setPositionToDelete(null);
            setTransferTargetId('');
            setForceTransferRequired(false);
            setMessage(
                transferTargetId
                    ? `"${positionToDelete.name}" pozisyonundaki personeller hedef pozisyona aktarıldı ve pozisyon silindi.`
                    : `"${positionToDelete.name}" pozisyonu başarıyla silindi.`
            );
            await fetchPositions();
        } catch (err) {
            const msg = err.message || "Pozisyon silinirken bir hata oluştu.";
            if (msg.includes("personel") || msg.includes("arşivlenmiş") || msg.includes("aktar")) {
                setForceTransferRequired(true);
            }
            setDeleteModalError(msg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleTransferConfirm = async () => {
        if (!positionToTransfer) return;
        if (!transferOnlyTargetId) {
            setTransferModalError("Lütfen personellerin aktarılacağı hedef pozisyonu seçin.");
            return;
        }

        setActionLoading(true);
        setTransferModalError(null);
        try {
            const res = await apiClient(`Position/${positionToTransfer.id}/transfer`, {
                method: 'POST',
                body: {
                    targetPositionId: Number(transferOnlyTargetId)
                }
            });
            setIsTransferModalOpen(false);
            setPositionToTransfer(null);
            setTransferOnlyTargetId('');
            setMessage(res?.message || "Pozisyon personelleri başarıyla aktarıldı.");
            await fetchPositions();
        } catch (err) {
            setTransferModalError(err.message || "Personel aktarımı sırasında bir hata oluştu.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleFormSubmit = async (positionData) => {
        setActionLoading(true);
        setError(null);
        try {
            const method = positionData.id ? 'PUT' : 'POST';
            const endpoint = positionData.id ? `Position/${positionData.id}` : 'Position';
            await apiClient(endpoint, {
                method,
                body: positionData,
            });
            setMessage(`"${positionData.name}" pozisyonu başarıyla ${positionData.id ? 'güncellendi' : 'eklendi'}!`);
            setIsModalOpen(false);
            setSelectedPosition(null);
            await fetchPositions();
        } catch (err) {
            throw err;
        } finally {
            setActionLoading(false);
        }
    };

    const handleModalClose = () => {
        if (actionLoading) return;
        setIsModalOpen(false);
        setIsConfirmModalOpen(false);
        setIsTransferModalOpen(false);
        setSelectedPosition(null);
        setPositionToDelete(null);
        setPositionToTransfer(null);
        setTransferTargetId('');
        setTransferOnlyTargetId('');
        setDeleteModalError(null);
        setTransferModalError(null);
        setForceTransferRequired(false);
    };

    const availableTargetPositionsForDelete = useMemo(() => {
        if (!positionToDelete) return [];
        return positionList.filter(p => p.id !== positionToDelete.id && p.isActive);
    }, [positionList, positionToDelete]);

    const availableTargetPositionsForTransfer = useMemo(() => {
        if (!positionToTransfer) return [];
        return positionList.filter(p => p.id !== positionToTransfer.id && p.isActive);
    }, [positionList, positionToTransfer]);

    const positionColumns = useMemo(() => {
        const columns = [
            {
                header: 'Pozisyon / Ünvan Adı',
                accessor: 'name',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            row.isActive
                                ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                        }`}>
                            <Briefcase className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-semibold text-gray-900 dark:text-gray-100">{row.name}</div>
                            <div className="text-[11px] text-gray-400 dark:text-gray-500">Kod: #{row.id}</div>
                        </div>
                    </div>
                )
            },
            {
                header: 'Açıklama',
                accessor: 'description',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <span className="text-gray-600 dark:text-gray-300 text-xs whitespace-normal line-clamp-2 max-w-md">
                        {row.description || '-'}
                    </span>
                )
            },
            {
                header: 'Personel Sayısı',
                accessor: 'activeEmployeeCount',
                sortable: true,
                searchable: false,
                getFilterValue: (row) => String(row.activeEmployeeCount ?? 0),
                render: (row) => {
                    const active = row.activeEmployeeCount || 0;
                    const inactive = row.inactiveEmployeeCount || 0;
                    return (
                        <div className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                active > 0
                                    ? 'bg-blue-50 text-blue-700 border border-blue-200/80 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                                <Users className="w-3.5 h-3.5" />
                                {active.toLocaleString('tr-TR')} Aktif
                            </span>
                            {inactive > 0 && (
                                <span
                                    className="text-[11px] text-gray-400 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/70 px-2 py-0.5 rounded-full"
                                    title="İşten ayrılmış / pasif personel sayısı"
                                >
                                    +{inactive} Pasif
                                </span>
                            )}
                        </div>
                    );
                }
            },
            {
                header: 'Durum',
                accessor: 'isActive',
                sortable: true,
                searchable: false,
                getFilterValue: (row) => row.isActive ? 'Aktif' : 'Pasif',
                render: (row) => row.isActive ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aktif
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                        <XCircle className="w-3.5 h-3.5" /> Pasif
                    </span>
                )
            },
        ];

        if (canUpdate || canDelete) {
            columns.push({
                header: 'Aksiyonlar',
                accessor: 'actions',
                filterable: false,
                render: (row) => (
                    <div className="flex items-center space-x-1.5">
                        {canUpdate && (
                            <button
                                onClick={() => handleEditClick(row)}
                                className="p-1.5 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
                                title="Pozisyonu Düzenle"
                            >
                                <Edit className="w-4 h-4" />
                            </button>
                        )}
                        {canUpdate && (row.totalEmployeeCount || 0) > 0 && (
                            <button
                                onClick={() => handleTransferClick(row)}
                                className="p-1.5 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors"
                                title="Personelleri Başka Pozisyona Aktar"
                            >
                                <ArrowRightLeft className="w-4 h-4" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => handleDeleteRequest(row)}
                                className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-gray-700 transition-colors"
                                title="Pozisyonu Sil"
                            >
                                <Trash className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ),
            });
        }
        return columns;
    }, [canUpdate, canDelete]);

    if (!canView && !loading) {
        return (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-xl flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 shrink-0" />
                Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.
            </div>
        );
    }

    const deleteNeedsTransfer = positionToDelete && (forceTransferRequired || (positionToDelete.totalEmployeeCount || 0) > 0);

    return (
        <div className="p-1 space-y-4">
            {/* Üst Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Toplam Pozisyon</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.total.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{summaryStats.activePos} Aktif</span>
                            {summaryStats.passivePos > 0 && ` • ${summaryStats.passivePos} Pasif`}
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                        <Briefcase className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Pozisyonlardaki Aktif Personel</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.totalActiveEmployees.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Ünvan atanmış toplam aktif personel
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                        <Users className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Aktif Çalışanı Olan Pozisyon</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {summaryStats.occupiedPos.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            En az 1 aktif personeli bulunan ünvanlar
                        </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6" />
                    </div>
                </div>
            </div>

            {message && (
                <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 p-3.5 rounded-xl flex items-center justify-between">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2.5 text-emerald-500 shrink-0" />
                        <span className="text-sm font-medium">{message}</span>
                    </div>
                    <button onClick={() => setMessage(null)} className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline ml-4">Kapat</button>
                </div>
            )}

            {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 p-3.5 rounded-xl flex items-center justify-between">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2.5 text-red-500 shrink-0" />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                    <button onClick={() => setError(null)} className="text-xs text-red-600 dark:text-red-400 hover:underline ml-4">Kapat</button>
                </div>
            )}

            <DataTable
                data={positionList}
                columns={positionColumns}
                itemsPerPage={10}
                pageSizeOptions={[10, 20, 50, 100]}
                showSearch={true}
                searchPlaceholder="Pozisyon adı veya açıklamada ara..."
                showPagination={true}
                emptyMessage="Henüz hiçbir pozisyon eklenmemiş."
                isLoading={loading}
                errorMessage={null}
                actionsHeader={
                    canCreate && (
                        <button
                            onClick={handleAddClick}
                            className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
                        >
                            <PlusCircle className="w-4 h-4 mr-2" />
                            Yeni Pozisyon Ekle
                        </button>
                    )
                }
            />

            {/* Pozisyon Ekle/Düzenle Modalı */}
            <InfoModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                title={selectedPosition ? `Pozisyon Düzenle: ${selectedPosition.name}` : "Yeni Pozisyon Ekle"}
                showFooter={false}
            >
                <PositionForm
                    position={selectedPosition}
                    onSave={handleFormSubmit}
                    onCancel={handleModalClose}
                    isNew={!selectedPosition}
                    submitting={actionLoading}
                />
            </InfoModal>

            {/* Pozisyon Personellerini Başka Pozisyona Aktar Modalı (Silmeden) */}
            <InfoModal
                isOpen={isTransferModalOpen}
                onClose={handleModalClose}
                title="Pozisyon Personellerini Aktar"
                type="warning"
                onConfirm={handleTransferConfirm}
                isLoading={actionLoading}
                confirmText="Aktarımı Başlat"
                cancelText="Vazgeç"
                confirmDisabled={!transferOnlyTargetId}
            >
                {positionToTransfer && (
                    <div className="space-y-4">
                        {transferModalError && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3 rounded-xl text-xs">
                                {transferModalError}
                            </div>
                        )}
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>{positionToTransfer.name}</strong> pozisyonundaki{' '}
                            <strong>{positionToTransfer.totalEmployeeCount || 0} personeli</strong> başka bir pozisyona topluca aktarmak için hedef pozisyonu seçin:
                        </p>

                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                Hedef Pozisyon Seçin <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={transferOnlyTargetId}
                                onChange={(e) => setTransferOnlyTargetId(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">-- Aktarılacak Pozisyonu Seçin --</option>
                                {availableTargetPositionsForTransfer.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} ({p.activeEmployeeCount || 0} Aktif Personel)
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </InfoModal>

            {/* Akıllı Silme & Aktararak Silme Onay Modalı */}
            <InfoModal
                isOpen={isConfirmModalOpen}
                onClose={handleModalClose}
                title="Pozisyon Silme Onayı"
                type="confirm"
                onConfirm={handleDeleteConfirm}
                isLoading={actionLoading}
                confirmText={deleteNeedsTransfer ? "Personelleri Aktar ve Pozisyonu Sil" : "Evet, Pozisyonu Sil"}
                cancelText="Vazgeç"
                confirmDisabled={deleteNeedsTransfer && !transferTargetId}
            >
                {positionToDelete && (
                    <div className="space-y-4">
                        {deleteModalError && (
                            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3 rounded-xl text-xs">
                                {deleteModalError}
                            </div>
                        )}

                        {deleteNeedsTransfer ? (
                            <>
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 p-3.5 rounded-xl text-xs space-y-1.5">
                                    <p className="font-semibold text-sm">
                                        "{positionToDelete.name}" pozisyonuna bağlı personeller bulundu!
                                    </p>
                                    <p>
                                        Bu pozisyonda <strong>{positionToDelete.activeEmployeeCount || 0} aktif</strong>
                                        {(positionToDelete.inactiveEmployeeCount || 0) > 0 && `, ${positionToDelete.inactiveEmployeeCount} pasif`} personel bulunmaktadır.
                                    </p>
                                    <p>
                                        Pozisyonu silebilmek için bağlı personellerin aktarılacağı yeni pozisyonu seçin:
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Personellerin Aktarılacağı Hedef Pozisyon <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={transferTargetId}
                                        onChange={(e) => setTransferTargetId(e.target.value)}
                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">-- Hedef Pozisyon Seçin --</option>
                                        {availableTargetPositionsForDelete.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name} ({p.activeEmployeeCount || 0} Aktif Personel)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        ) : (
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>"{positionToDelete.name}"</strong> pozisyonunu kalıcı olarak silmek istediğinizden emin misiniz?
                            </p>
                        )}
                    </div>
                )}
            </InfoModal>
        </div>
    );
}
